import { drawnTopic } from './flow-issues';
import type { ConsoleState } from './store';
import type { RouteDef, RouteIngress, Sink } from './types';

export interface TenantDestination {
  routeId: string;
  tenantId: string;
  destination: { url?: string; headers?: Record<string, string | null> };
}

export interface DeliveryRevision {
  routeId: string;
  version: number;
  transformationVersion: number;
  transformationHash: string;
  publishedAt: string;
  publishedBy: string;
  live: boolean;
  shared?: Sink;
  tenants: Record<string, Sink>;
}

export interface DeliveryState {
  tenantIngress?: { routeId: string; tenantId: string; ingress: Partial<RouteIngress> }[];
  tenantDestinations?: TenantDestination[];
  deliveryRevisions?: DeliveryRevision[];
}

/** Resolve at publication time; the runtime receives complete four-segment targets. */
/**
 * The platform's own topics, which are never where a document goes: a worker writing to its own
 * in-tray would read the document back and process it forever. The data plane's defaults and the
 * names it replaced; a deployment that renames them should keep this list in step.
 */
export const PLATFORM_TOPICS = [
  'integration.events',
  'integration.events.failed',
  'platform.ingest.accepted',
  'platform.delivery.pending',
  'platform.delivery.dlq',
];

export const platformTopicFault = (sink?: { kind?: unknown; target?: unknown } | null): string | undefined => {
  const target = typeof sink?.target === 'string' ? sink.target.trim() : '';
  return sink?.kind === 'kafka' && PLATFORM_TOPICS.includes(target)
    ? `${target} is one of the platform's own topics. Choose the topic the bills should finally go to.`
    : undefined;
};

/**
 * Where an endpoint's bills go: the topic its drawn flow ends at, then its own choice, then its
 * vendor's, then the one Casa sends every bill to.
 */
export const destinationOf = (
  state: Pick<ConsoleState, 'vendors'> & { defaultDestination?: Sink },
  route: Pick<RouteDef, 'sink' | 'vendorId'> | undefined,
  flow?: string,
): Sink | undefined => {
  const drawn = drawnTopic(flow, PLATFORM_TOPICS);
  if (drawn) return { kind: 'kafka', target: drawn };
  const chosen = route?.sink ?? state.vendors.find((vendor) => vendor.id === route?.vendorId)?.defaultSink;

  return chosen?.target.trim() ? chosen : state.defaultDestination;
};

export const mergeDestination = (
  shared: Sink | undefined,
  patch?: TenantDestination['destination'],
): Sink | undefined => {
  if (!shared && !patch?.url) return undefined;
  const headers: Record<string, string> = Object.create(null);
  for (const [name, value] of Object.entries(shared?.headers ?? {})) headers[name] = value;
  for (const [name, value] of Object.entries(patch?.headers ?? {})) {
    for (const existing of Object.keys(headers))
      if (existing.toLowerCase() === name.toLowerCase()) delete headers[existing];
    if (value !== null) headers[name] = value;
  }
  return { kind: patch?.url ? 'http' : shared!.kind, target: patch?.url ?? shared!.target, headers };
};

export const draftDestination = (state: ConsoleState & DeliveryState, routeId: string, tenantId?: string) => {
  const route = state.routes.find((item) => item.id === routeId);
  const patch = state.tenantDestinations?.find((item) => item.routeId === routeId && item.tenantId === tenantId);
  return mergeDestination(destinationOf(state, route), patch?.destination);
};

export const deliveryMatrix = (state: ConsoleState & DeliveryState, routeId: string): Record<string, Sink> => {
  const route = state.routes.find((item) => item.id === routeId);
  const vendor = state.vendors.find((item) => item.id === route?.vendorId);
  return Object.fromEntries(
    (vendor?.tenantIds ?? []).flatMap((id) => {
      const target = draftDestination(state, routeId, id);
      return target ? [[id, target]] : [];
    }),
  );
};

export const saveDestination = (
  state: ConsoleState & DeliveryState,
  routeId: string,
  tenantId: string,
  destination: TenantDestination['destination'] | null,
): ConsoleState & DeliveryState => {
  const route = state.routes.find((item) => item.id === routeId);
  if (!route) throw new Error('Route not found.');
  if (!state.vendors.find((item) => item.id === route.vendorId)?.tenantIds.includes(tenantId))
    throw new Error('Connect this tenant to the vendor first.');
  if (destination) {
    if (
      destination.headers !== undefined &&
      (!destination.headers || typeof destination.headers !== 'object' || Array.isArray(destination.headers))
    )
      throw new Error('Headers must be a JSON object.');
    if (destination.url === undefined && Object.keys(destination.headers ?? {}).length === 0) destination = null;
  }
  if (destination) {
    if (destination.url !== undefined) {
      const url = new URL(destination.url);
      if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password)
        throw new Error('Use an HTTP URL without embedded credentials.');
    }
    for (const [name, value] of Object.entries(destination.headers ?? {})) {
      if (
        !/^[!#$%&'*+.^_`|~0-9a-z-]+$/i.test(name) ||
        (value !== null && (typeof value !== 'string' || /[\r\n]/.test(value)))
      )
        throw new Error('Use valid HTTP header names and single-line values; null removes an inherited header.');
    }
    const names = Object.keys(destination.headers ?? {}).map((name) => name.toLowerCase());
    if (new Set(names).size !== names.length) throw new Error('Header names must be unique, ignoring case.');
  }
  return {
    ...state,
    tenantDestinations: [
      ...(state.tenantDestinations ?? []).filter((item) => item.routeId !== routeId || item.tenantId !== tenantId),
      ...(destination ? [{ routeId, tenantId, destination: structuredClone(destination) }] : []),
    ],
    runs: state.runs.filter((run) => run.routeId !== routeId),
  };
};
